import React, { useCallback, useContext } from 'react'

import CornerControls from './common/CornerControls'
import DetailCardLayout from './common/DetailCardLayout'
import ItemCardImage from './common/ItemCardImage'
import ItemTitle from './common/ItemTitle'
import NotesAndAttachments from './common/NotesAndAttachments'
import PrintOnlyAddressAndNotes from './common/PrintOnlyAddressAndNotes'
import { DetailItemCardVariant } from './common/types'
import ExperiencePriceAndCTAs from './PriceAndCTAs/ExperiencePriceAndCTAs'
import GooglePlaceRating from '../GooglePlaceRating/GooglePlaceRating'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import GeoContext from 'contexts/geoContext'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useExperience } from 'tripPlanner/hooks/api'
import { ItemMetadata } from 'tripPlanner/models'
import {
  AttractionItem,
  RestaurantBarItem,
  EventItem,
  ExperienceItem,
  ThingToDoItem,
} from 'tripPlanner/types/tripItem'
import { getDisplayableCustomThingToDoName } from 'tripPlanner/utils/itemConversions/common'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { formatShortDateRange, isCustomItem, isLeBookmark } from 'tripPlanner/utils'
import useNavigateTo from 'hooks/useNavigateTo'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import OutsideTripDatesIcon from '../Itinerary/OutsideTripDatesIcon'
import EditTripItem from '../TripModal/TripItems/Edit'
import LineNotesIcon from 'components/Luxkit/Icons/line/LineNotesIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { NarrowCardOnlyBookedLabel } from './common/BookedLabel'
import SuggestedLabel from './common/SuggestedLabel'
import CustomSuggestionCTAs from './PriceAndCTAs/CustomSuggestionCTAs'
import DistanceToAccommodation from './common/DistanceToAccommodation'
import ItemCardImageCarousel from './common/ItemCardImageCarousel'
import { NarrowCardOnlyJustAddedLabel } from './common/JustAddedLabel'
import { useJustAddedTripItem } from 'tripPlanner/hooks/useJustAddedTripItem'
import Group from 'components/utils/Group'

interface Props {
  item:
    | AttractionItem
    | RestaurantBarItem
    | EventItem
    | ThingToDoItem
    | ExperienceItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailThingToDoCard({ item, variant, tripNameLabel }: Props) {
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const viewAsTemplate = !!tripContext?.viewAsTemplate
  const Icon = ItemMetadata[item.type].icon

  const place = 'startPlace' in item ? item.startPlace : item.place
  const placeId = 'id' in place ? place.id : undefined

  const title =
    item.type === 'EXPERIENCE' ?
      item.name :
      getDisplayableCustomThingToDoName(item)

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const experienceId = item.type === 'EXPERIENCE' ? item.code : undefined
  const { data: experience } = useExperience({
    experienceId,
    currentCurrency,
    currentRegionCode,
  })

  const timeDescription = formatShortDateRange(item)

  let imgId: string | undefined = undefined
  let imgUrl: string | undefined = place.photoUrl

  let offerLink: string | undefined = undefined
  if (item.type === 'EXPERIENCE') {
    imgId = item.savedItemData?.imageIds?.[0] ?? experience?.images[0]?.id
    imgUrl = experience?.images[0]?.url
    offerLink = isLeBookmark(item) ? buildProductLink(item, !viewAsTemplate, { experience }) : undefined
  }

  const openModal = useOpenModal()
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const navigateTo = useNavigateTo()

  const onClickAddTripItemDates = useCallback(() => {
    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: tripItemSelectionId(item.id) })
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const dateWarningIcon =
  variant !== 'drawer' ?
    <OutsideTripDatesIcon
      item={item}
      changeItemDates={onClickAddTripItemDates}
    /> :
    undefined

  const recentlyAdded = useJustAddedTripItem(item)

  return (
    <DetailCardLayout data-testid="detail-card-thing-to-do">
      {item.isSuggested && experience && experience.images.length > 1 && (
        <DetailCardLayout.Carousel>
          <ItemCardImageCarousel images={experience.images} />
        </DetailCardLayout.Carousel>
      )}
      <DetailCardLayout.Image>
        <ItemCardImage
          item={item}
          name={place.name}
          placeId={placeId}
          imgId={imgId}
          imgSrc={imgUrl}
        />
      </DetailCardLayout.Image>
      <DetailCardLayout.Content>
        {canEdit && <CornerControls item={item} variant={variant} offerLink={offerLink} />}
        <Group direction="horizontal" gap={4}>
          {item.isBooked && <NarrowCardOnlyBookedLabel className="bottom-margin" />}
          {recentlyAdded && <NarrowCardOnlyJustAddedLabel className="bottom-margin" />}
        </Group>

        <VerticalSpacer gap={8}>
          {item.isSuggested && <SuggestedLabel />}
          <VerticalSpacer gap={4}>
            <BodyText variant="small" colour="neutral-two">
              {place.region}
            </BodyText>
            <ItemTitle icon={Icon}>{title}</ItemTitle>
            {placeId && <GooglePlaceRating placeId={placeId} size="S" />}
          </VerticalSpacer>
          {item.isSuggested && <DistanceToAccommodation item={item} />}
          {timeDescription && (
            <BodyTextBlock variant="medium" startIcon={<LineNotesIcon />} endIcon={dateWarningIcon}>
              {timeDescription}
            </BodyTextBlock>
          )}
          {tripNameLabel}
          <NotesAndAttachments
            item={item}
            canEdit={canEdit}
            variant={variant}
          />
          {item.type === 'EXPERIENCE' && experience && (
            <ExperiencePriceAndCTAs
              item={item}
              experience={experience}
              viewAsTemplate={viewAsTemplate}
              variant={variant}
            />
          )}
          {item.type !== 'EXPERIENCE' && item.isSuggested && canEdit && (
            <CustomSuggestionCTAs item={item} />
          )}
          <PrintOnlyAddressAndNotes item={item} />
        </VerticalSpacer>
      </DetailCardLayout.Content>
    </DetailCardLayout>
  )
}

export default DetailThingToDoCard
